import React from 'react';

import './AuTable.scss';

interface IAuTableProps {
  caption?: string | React.ReactNode;
  headers: Array<ITableHeader>;
  data: Array<any>;
  striped?: boolean;
  className?: string;
  firstCellIsHeader?: boolean;
  style?: React.CSSProperties;
}

interface ITableHeader {
  title?: string;
  key: string;
  type?: 'text' | 'numeric';
  width?: '10' | '20' | '25' | '33' | '50' | '75';
  render?: any;
}

const AuTable: React.FC<IAuTableProps> = ({ caption, headers, data, striped, firstCellIsHeader, className = '', ...attributeOptions }) => {
  return (
    <table className={`au-table ${striped ? 'au-table--striped ' : ' '} ${className}`} {...attributeOptions}>
      {
        caption && <AuTableCaption tableCaption={caption} />
      }
      <AuTableHead>
        <AuTableRow>
          {
            headers.map((header, index) => (
              <AuTableHeader
                title={header.title}
                key={index}
                type={header.type ?? 'text'}
                width={header.width}
              />
            ))
          }
        </AuTableRow>
      </AuTableHead>
      <AuTableBody>
        {data.map((row, rowIndex) => (
          <AuTableRow key={rowIndex}>
            {
              headers.map((header, columnIndex) => {
                // check to render first cell in rows as a header or not
                if (columnIndex === 0 && firstCellIsHeader === true) {
                  return <AuTableHeader type='text' key={columnIndex} scope='row' title={row[header.key] ? row[header.key] : ''} />;
                }

                return (
                  <AuTableCell
                    key={columnIndex}
                    data={row[header.key] ? row[header.key] : ''}
                    type={header.type ?? 'text'}
                    render={header.render ? () => header.render(row[header.key], row, rowIndex) : undefined}
                  />
                );
              })
            }
          </AuTableRow>
        ))}
      </AuTableBody>
    </table>
  );
};

export default AuTable;

interface IAuTableBodyProps {
  className?: string;
}

/**
 * The table body component
 *
 */
export const AuTableBody: React.FC<IAuTableBodyProps> = ({ className = '', ...attributeOptions }) => {
  return (
    <tbody className={`au-table__body ${className} `} {...attributeOptions}>

    </tbody>
  );
};

interface ITableHeadProps {
  className?: string;
}

/**
 * The table head component
 *
 */
export const AuTableHead: React.FC<ITableHeadProps> = ({ children, className = '', ...attributeOptions }) => {
  return (
    <thead className={`au-table__head ${className}`} {...attributeOptions}>
      {children}
    </thead>
  );
};


interface IAuTableHeaderProps {
  title?: string;
  className?: string;
  type: 'text' | 'numeric';
  width?: '10' | '20' | '25' | '33' | '50' | '75';
  scope?: 'row' | 'col';
  attributeOptions?: any;
}

/**
 * The table header component
 *
 */
export const AuTableHeader: React.FC<IAuTableHeaderProps> = ({ title, type, width, scope = 'row', className = '', ...attributeOptions }) => (
  <th
    className={`au-table__header ${className}` +
    `${type === 'numeric' ? ' au-table__header--numeric ' : ' '}` +
    `${width ? ' au-table__header--width-' + width : ' '} `}
    scope={scope}
    {...attributeOptions}
  >
    {title}
  </th>
);

interface IAuTableCellProps {
  data: string;
  type: 'text' | 'numeric';
  className?: string;
  render?: () => any;
  attributeOptions?: any;
}

/**
 * The data/cell component
 *
 */
export const AuTableCell: React.FC<IAuTableCellProps> = ({ data, type, render, className = '', ...attributeOptions }) => (
  <td className={`au-table__cell ${className} ${type === 'numeric' ? 'au-table__cell--numeric ' : ' '}`}
    {...attributeOptions}
  >
    {
      render ? render() : data
    }
  </td>
);

interface IAuTableRowProps {
  className?: string;
  attributeOptions?: any;
}
/**
 * The table row component
 */
export const AuTableRow: React.FC<IAuTableRowProps> = ({ children, className = '', ...attributeOptions }) => (
  <tr className={`au-table__row ${className}`} {...attributeOptions}>
    {children}
  </tr>
);

interface IAuTableCaptionProps {
  className?: string;
  tableCaption: string | React.ReactNode;
  attributeOptions?: any;
}

/**
 * The table caption component
 *
 */
export const AuTableCaption: React.FC<IAuTableCaptionProps> = ({ tableCaption, className, ...attributeOptions }) => (
  <caption className={`au-table__caption ${className}`} {...attributeOptions}>
    {tableCaption}
  </caption>
);
